import Styles from './WorkflowBuilderItem.module.scss'
import cx from 'classnames'
import { ColorInput } from 'common/PropertyForm/ColorInput'
import { observer } from 'mobx-react-lite'
import { ChangeEventHandler, useEffect, useRef, useState } from 'react'
import { State } from 'stores/Workflows/State'
import { useFocusWithin, usePress } from '@react-aria/interactions'
import { Divider } from './Divider'
import { AutoResizingTextInput } from './AutoResizingInput'
import { Chip } from './Chip'
import { Description } from './Description'

const NAME_MAX_CHARS = 50

interface Props {
  state: State
}

export const Form = observer(({ state }: Props) => {
  const [isEditing, setEditing] = useState(false)
  const nameInput = useRef<HTMLInputElement | null>(null)

  const focusNameInput = () => {
    if (!nameInput.current) {
      return
    }
    nameInput.current.focus()
  }

  const { pressProps } = usePress({
    onPress: () => {
      setEditing(true)
      focusNameInput()
    },
  })

  const { focusWithinProps } = useFocusWithin({
    onFocusWithinChange: (focusWithin) => {
      setEditing(focusWithin)
      if (!focusWithin) {
        state.setFocus(false)
      }
    },
  })

  // Set name to Untitled if submitting an empty name
  useEffect(() => {
    if (!isEditing && !state.value) {
      state.setName('Untitled')
    }
  }, [isEditing, state])

  // When setting focus on the state instance itself, focus on the name. This is
  // the case, for example, when we create a new state by pressing on an
  // external button
  useEffect(() => {
    if (state.focus) {
      focusNameInput()
    }
  }, [state.focus])

  const handleNameChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    state.setName(event.target.value.slice(0, NAME_MAX_CHARS))
  }

  const handleDescriptionChange: ChangeEventHandler<HTMLInputElement> = (event) =>
    state.setDescription(event.target.value)

  return (
    <div className={Styles.form}>
      <div {...focusWithinProps} className={Styles.propertyValueFieldset}>
        <div className={cx(Styles.chipFieldset, { [Styles.hidden]: !isEditing })}>
          <ColorInput selectedColor={state.color} onSelect={state.setColor} />
          <AutoResizingTextInput value={state.value} onChange={handleNameChange} ref={nameInput} />
        </div>
        {!isEditing && (
          <span {...pressProps}>
            <Chip state={state} />
          </span>
        )}
        <Divider />
        <Description value={state.description} onChange={handleDescriptionChange} />
      </div>
    </div>
  )
})
